<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="container mt-4 mb-4">
    <div style="max-width:560px">
        <h3>Kontoinnstillinger for Unimicro</h3>
        <div v-if='updated'>
          <span class='message-info'>Kontoinnstillingene er oppdatert</span>
        </div>
        <div v-if='errorMessage'>
          <span class='message-error'>{{ errorMessage }}</span>
        </div>

        <div v-if="locations.length === 0 && !loading" class="mt-4">
          <span class="message-info">Ingen avdelinger har Unimicro aktivert. Aktiver det under Avdelinger.</span>
        </div>

        <div v-if="locations.length > 0 && hasMultiLocations()" class="select-location-box mt-4">
          <span class="ingredient-title">For avdeling</span>
          <select class="form-select" v-model="locationId" @change="getAccountSettings()">
            <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
          </select>
        </div>

        <template v-if="locations.length > 0">
          <!-- Locked when this location's Unimicro connection is live. These are not cosmetic settings:
               a department, an account number and the auto-sync toggle decide what gets posted to a real
               ledger and where it lands. The environment lives on the account document, so it is fetched
               alongside — see loadEnvironment(). -->
          <config-lock ref="lock" :configured="accountIsProduction">
          <div class="mt-4">
            <span class='ingredient-title'>Inntektskonto 25 % (høy sats)</span>
            <input class='form-control' type='number' v-model.number='accountSettings.revenueAccountNumber25' />
          </div>

          <div class="mt-3">
            <span class='ingredient-title'>Inntektskonto 15 % (middels sats — mat)</span>
            <input class='form-control' type='number' v-model.number='accountSettings.revenueAccountNumber15' />
          </div>

          <div class="mt-3">
            <span class='ingredient-title'>Inntektskonto 0 % (avgiftsfritt)</span>
            <input class='form-control' type='number' v-model.number='accountSettings.revenueAccountNumber0' />
            <!-- Unlike tips and bonus, these are NEVER created for you: a revenue account that does not
                 exist stops the sync with a named error, which is the right outcome. The dangerous case
                 is the opposite one — a number that exists but is the wrong account posts perfectly and
                 surfaces only in the annual accounts — so a pinned number is used exactly as typed and
                 is not quietly replaced by the standard one. -->
            <div class="mt-2" style="font-size:0.85em; color:var(--bo-muted);">
              Standardkontoene er fylt inn. Endre dem bare hvis kontoplanen i Unimicro bruker andre
              kontoer &mdash; f.eks. <strong>3030</strong> for mat med 15&nbsp;%. Kontoen må finnes
              i kontoplanen fra før; synkroniseringen oppretter den ikke, og stopper med feilmelding
              hvis den mangler. <strong>0</strong> tilbakestiller til standard.
            </div>
          </div>

          <div class="mt-4">
            <span class='ingredient-title'>Tips konto</span>
            <input class='form-control' type='number' v-model.number='accountSettings.tipsAccountNumber' />
          </div>

          <div class="mt-3">
            <span class='ingredient-title'>Bonus konto</span>
            <input class='form-control' type='number' v-model.number='accountSettings.bonusAccountNumber' />
          </div>

          <div class="mt-3">
            <span class='ingredient-title'>Avdeling</span>
            <!-- A PICKER, not a number field. Unimicro shows a department by its NUMBER ("1 - Osushi
                 Grorud") while the journal line wants its ID, and the two are not the same value —
                 so asking anyone to type "the ID" invites the one mistake that cannot be seen
                 afterwards: the entry posts, the amounts are right, and the department is silently
                 wrong or absent. Showing Unimicro's own label and storing the ID behind it removes
                 the question entirely. -->
            <select v-if="departments.length > 0" class="form-select"
                    v-model.number="accountSettings.departmentId">
              <option :value="0">Ingen avdeling</option>
              <option v-for="dept in departments" :key="dept.ID" :value="dept.ID">
                {{ departmentLabel(dept) }}
              </option>
              <!-- A saved ID that is no longer in Unimicro's list keeps its own option rather than
                   leaving the select blank. Blank would look like «Ingen avdeling» and quietly save as
                   0 the next time anyone touched an unrelated field on this page. -->
              <option v-if="unknownDepartmentId" :value="unknownDepartmentId">
                Ukjent avdeling (ID {{ unknownDepartmentId }}) &mdash; finnes ikke i Unimicro
              </option>
            </select>

            <!-- Fallback: the list needs a live, ACTIVE Unimicro connection. Without one a select with
                 no options would make the setting uneditable, so the raw ID field stays reachable. -->
            <input v-else class='form-control' type='number'
                   v-model.number='accountSettings.departmentId' />

            <div class="mt-2" style="font-size:0.85em; color:var(--bo-muted);">
              <template v-if="departments.length > 0">
                Bokf&oslash;ringen merkes med denne avdelingen i Unimicro. Velg
                <strong>Ingen avdeling</strong> hvis regnskapet ikke bruker avdelinger.
              </template>
              <template v-else>
                Fant ingen avdelinger i Unimicro &mdash; skriv inn verdien fra <strong>ID</strong>-kolonnen
                under Innstillinger &rarr; Unimicro &rarr; Avdelinger, ikke &laquo;Nummer&raquo;, som er
                den Unimicro viser i sitt eget grensesnitt. La feltet st&aring; p&aring; 0 hvis
                regnskapet ikke bruker avdelinger.
              </template>
            </div>
          </div>

          <div class="form-switch-wrapper mt-4">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="accountSettings.autoSyncEnabled" />
                <i></i>
                <span class="bold ms-2">Automatisk synkronisering</span>
              </div>
            </label>
          </div>

          <div class='bo_hr mt-4'>
            <hr/>
          </div>

          <div class='mb-5'>
            <button type='button' class='bo_btn' @click='save'> Lagre </button>
          </div>
          </config-lock>
        </template>
    </div>
  </div>
</template>

<script>
  import SettingsService from '@/services/settings/SettingsService';
  import MerchantUtil from "@/util/MerchantUtil";
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";
  import ConfigLock from "@/components/other/ConfigLock.vue";
  import AccountingEnv from "@/util/AccountingEnv";

  export default {
    components: {LoadingSpinner, ConfigLock},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],
        // Starts true so the form is locked before we know: the environment arrives asynchronously, and
        // an unlocked moment on a production config is the failure this whole guard is against.
        accountIsProduction: true,
        updated: false,
        errorMessage: '',
        departments: [],
        accountSettings: {
          type: "unimicro_account_settings",
          merchant: '',
          locationId: '',
          revenueAccountNumber25: 3000,
          revenueAccountNumber15: 3020,
          revenueAccountNumber0: 3100,
          tipsAccountNumber: 3902,
          bonusAccountNumber: 3903,
          departmentId: 0,
          autoSyncEnabled: false
        }
      };
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data.filter(l => l.accountingSystem === 'UNIMICRO');
            this.locationId = this.locations.length ? this.locations[0].id : '';

            if (this.locationId) {
              this.getAccountSettings();
            } else {
              this.loading = false;
            }
          },
          (error) => {
            console.log(error);
          }
      );
    },
    computed: {
      /**
       * The saved department ID when Unimicro no longer lists it, else 0.
       *
       * Without this the select has no matching option, renders empty, and the first save turns a
       * real (if stale) setting into «Ingen avdeling» without anyone choosing that.
       */
      unknownDepartmentId(){
        const id = this.accountSettings.departmentId;
        if (!id) return 0;
        return this.departments.some(dept => dept.ID === id) ? 0 : id;
      }
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      /** Unimicro's own wording — «1 - Osushi Grorud» — so the option is recognisable from their UI. */
      departmentLabel(dept){
        const number = dept.DepartmentNumber;
        return (number ? number + ' - ' : '') + (dept.Name || '');
      },
      /**
       * The department list for the picker. Needs a live, ACTIVE connection, so an empty list is the
       * normal state before setup — the template falls back to the raw ID field rather than blocking.
       */
      loadDepartments(){
        this.departments = [];
        SettingsService.getUnimicroDepartments(this.locationId).then(
            (response) => {
              this.departments = Array.isArray(response.data) ? response.data : [];
            },
            (error) => {
              console.log(error);
            }
        );
      },
      /**
       * Which environment this location's connection points at.
       *
       * It lives on the ACCOUNT document, not on these settings, so it has to be fetched separately.
       * Fails safe in both directions: an error leaves accountIsProduction true, so an unreachable
       * backend locks the form rather than opening it.
       */
      loadEnvironment(){
        SettingsService.getUnimicroAccount(this.locationId).then(
            (response) => {
              const acc = response.data || {};
              this.accountIsProduction = AccountingEnv.isProduction(acc.baseApiUrl, acc.loginServerUrl);
              this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());
            },
            (error) => {
              console.log(error);
              this.accountIsProduction = true;
              this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());
            }
        );
      },
      getAccountSettings(){
        this.loading = true;
        // One entry point, so mounted() and the location selector cannot get different answers.
        this.loadEnvironment();
        this.loadDepartments();
        this.updated = false;
        this.errorMessage = '';
        SettingsService.getUnimicroAccountSettings(this.locationId).then(
            (response) => {
              if(response.data !== null){
                // Fill in keys a document saved before the revenue-account fields existed does not
                // have. Without this they arrive undefined and render as empty boxes, which reads as
                // «no default» rather than «uses the standard account».
                this.accountSettings = Object.assign({}, this.$options.data().accountSettings, response.data);
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false;
            }
        );
      },
      save(){
        this.loading = true;
        this.updated = false;
        this.errorMessage = '';
        this.accountSettings.merchant = MerchantUtil.getMerchant();
        this.accountSettings.locationId = this.locationId;

        SettingsService.editUnimicroAccountSettings(this.accountSettings).then(
            (response) => {
              let documentId = response.data;
              this.accountSettings.id = documentId;
              if(documentId){
                this.updated = true;
              } else {
                this.errorMessage = 'Lagring feilet';
              }
              this.loading = false;
              // Back to the lock the saved config calls for: production re-locks, test stays open.
              this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());
            },
            (error) => {
              console.log(error);
              this.errorMessage = 'Lagring feilet';
              this.loading = false;
            }
        );
      }
    }
  };
</script>
